import { ReactNode, useId, useState } from 'react';
import { Box, Button, Collapse, ListItem } from '@mui/material';
import { ChevronDownIcon } from '../../componentsv2/icons/ChevronDownIcon';
import { ChevronRightIcon } from '../../componentsv2/icons/ChevronRightIcon';
import Link from 'next/link';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';

export interface MainSidebarItemProps {
  active?: boolean;
  children?: ReactNode;
  chip?: ReactNode;
  depth: number;
  icon?: ReactNode;
  info?: ReactNode;
  open?: boolean;
  path?: string;
  isPathExternal?: boolean;
  title: string;
  extra?: ReactNode;
  [key: string]: any;
}

export const MainSidebarItem = ({
  active = false,
  children,
  chip,
  depth,
  icon,
  info,
  open: openProp = false,
  path,
  isPathExternal,
  title,
  extra,
  ...other
}: MainSidebarItemProps) => {
  const organization = useCommittedOrganization();
  const [open, setOpen] = useState(openProp);
  const childrenId = useId();
  const [previousOpenProp, setPreviousOpenProp] = useState(openProp);

  // Open a branch when navigation lands on one of its children; never force it closed, so a
  // branch the user expanded stays expanded.
  if (openProp !== previousOpenProp) {
    setPreviousOpenProp(openProp);
    if (openProp) {
      setOpen(true);
    }
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 24;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  // Branch
  if (children) {
    return (
      <ListItem
        disableGutters
        sx={{
          display: 'block',
          mb: 0,
          py: 0,
          px: 2,
        }}
        {...other}
      >
        <Button
          aria-controls={childrenId}
          aria-expanded={open}
          endIcon={!open ? <ChevronRightIcon fontSize='small' /> : <ChevronDownIcon fontSize='small' />}
          disableRipple
          onClick={handleToggle}
          startIcon={icon}
          sx={{
            color: active ? 'secondary.main' : 'neutral.300',
            justifyContent: 'flex-start',
            pl: `${paddingLeft}px`,
            pr: 3,
            textAlign: 'left',
            textTransform: 'none',
            width: '100%',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255, 0.08)',
            },
            '& .MuiButton-startIcon': {
              color: active ? 'secondary.main' : 'neutral.400',
            },
            '& .MuiButton-endIcon': {
              color: 'neutral.400',
            },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>{title}</Box>
          {info}
        </Button>
        <Collapse id={childrenId} in={open} sx={{ mt: 0.5 }}>
          {children}
        </Collapse>
      </ListItem>
    );
  }

  // Leaf
  return (
    <ListItem
      disableGutters
      sx={{
        display: 'flex',
        mb: 0.0,
        py: 0,
        px: 2,
      }}
    >
      <Button
        component={Link}
        href={isPathExternal ? path : `/${organization}${path}`}
        startIcon={icon}
        endIcon={chip}
        disableRipple
        target={isPathExternal ? '_blank' : undefined}
        rel={isPathExternal ? 'noopener noreferrer' : undefined}
        sx={{
          backgroundColor: active ? 'rgba(255,255,255, 0.08)' : undefined,
          borderRadius: 1,
          color: active ? 'secondary.main' : 'neutral.300',
          fontWeight: active ? 'fontWeightBold' : undefined,
          justifyContent: 'flex-start',
          pl: `${paddingLeft}px`,
          pr: 3,
          mb: 0.0,
          textAlign: 'left',
          textTransform: 'none',
          width: '100%',
          '& .MuiButton-startIcon': {
            color: active ? 'secondary.main' : 'neutral.400',
          },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255, 0.08)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', gap: 1 }}>
          {title} {extra}
        </Box>
        {info}
      </Button>
    </ListItem>
  );
};
